import { useEffect, useMemo, useState } from 'react';
import { ChartType } from '../screens/Detail/Charts/ProfitChart';

export type ChartDataPoint = {
  x: number;
  y: number;
};

const useAnimatedChartData = (
  id: string,
  dataPoints: Array<ChartDataPoint[]>,
  selectedChartTypes: ChartType[],
  useAnimatedDomain?: boolean,
  speed = 3,
) => {
  const [animatedDataPoints, setAnimatedDataPoints] = useState<
    Array<ChartDataPoint[]>
  >([...dataPoints.map((d) => [] as ChartDataPoint[])]);
  const [domain, setDomain] = useState<{
    x: [number, number];
    y: [number, number];
  }>({ x: [0, 0], y: [0, 0] });
  const [isAnimatedFinished, setIsAnimationFinished] = useState(false);

  useEffect(() => {
    if (!id) {
      return;
    }

    setIsAnimationFinished(false);
  }, [id]);

  useEffect(() => {
    if (!selectedChartTypes) {
      return;
    }

    setIsAnimationFinished(false);
  }, [selectedChartTypes]);

  useEffect(() => {
    if (isAnimatedFinished) {
      setAnimatedDataPoints(dataPoints);
    }

    if (dataPoints.length === animatedDataPoints.length) {
      return;
    }

    setIsAnimationFinished(false);
  }, [
    animatedDataPoints.length,
    dataPoints,
    dataPoints.length,
    isAnimatedFinished,
  ]);

  const hasData = useMemo(
    () => dataPoints.some((d) => !!d.length),
    [dataPoints],
  );

  // Earliest x value
  const earliestDataSetIndex = useMemo(() => {
    if (!hasData) {
      return 0;
    }

    return dataPoints.reduce(
      (acc, curr, index) => {
        if (!curr?.length) {
          return acc;
        }

        if (curr[0].x < acc.earliestTimestamp) {
          return { index, earliestTimestamp: curr[0].x };
        }

        return acc;
      },
      { index: 0, earliestTimestamp: Infinity },
    ).index;
  }, [dataPoints, hasData]);

  const earliestDataSetLength = useMemo(
    () => dataPoints[earliestDataSetIndex].length,
    [dataPoints, earliestDataSetIndex],
  );

  // Animation logic
  useEffect(() => {
    if (!hasData || isAnimatedFinished) {
      return;
    }

    const stepSize = Math.max(
      1,
      Math.floor(earliestDataSetLength / (speed * 30)),
    );
    let step = stepSize;

    const animate = () => {
      if (!hasData || isAnimatedFinished) {
        return;
      }

      const slicedLargestDataSetDataPoints = dataPoints[
        earliestDataSetIndex
      ].slice(0, step + 1);

      const xValues = slicedLargestDataSetDataPoints.map((d) => d.x);
      const slicedDataPoints = dataPoints.map((d) =>
        d.filter(
          (dp) => xValues[0] <= dp.x && dp.x <= xValues[xValues.length - 1],
        ),
      );

      const yValues = slicedDataPoints.reduce<number[]>((acc, curr) => {
        return [...acc, ...curr.map((d) => d.y)];
      }, []);

      const firstY = slicedLargestDataSetDataPoints[0].y;

      const minY = Math.min(...yValues);
      const maxY = Math.max(...yValues);

      // Update data
      setAnimatedDataPoints(slicedDataPoints);

      if (useAnimatedDomain) {
        setDomain({
          x: [Math.min(...xValues), Math.max(...xValues)],
          y: [minY, Math.max(firstY * 2, maxY)],
        });
      } else {
        const yValues = dataPoints.reduce<number[]>((acc, curr) => {
          return [...acc, ...curr.map((d) => d.y)];
        }, []);

        setDomain({
          x: [
            Math.min(...dataPoints[earliestDataSetIndex].map((d) => d.x)),
            Math.max(...dataPoints[earliestDataSetIndex].map((d) => d.x)),
          ],
          y: [Math.min(...yValues), Math.max(...yValues)],
        });
      }

      if (step >= earliestDataSetLength) {
        clearInterval(interval);
        setIsAnimationFinished(true);
      }

      step = step + stepSize;
    };

    const interval = setInterval(animate, speed); // Adjust speed (500ms per step)
    return () => clearInterval(interval);
  }, [
    dataPoints,
    speed,
    earliestDataSetIndex,
    earliestDataSetLength,
    hasData,
    isAnimatedFinished,
    useAnimatedDomain,
  ]);

  return {
    animatedDataPoints: hasData ? animatedDataPoints : [[]],
    domain,
    isAnimatedFinished,
  };
};

export default useAnimatedChartData;
